import { checkNameAvailability } from './checkNames';

/**
 * Extracts the base endpoint pattern from a full endpoint URL
 * This allows us to debounce based on the endpoint type, not the specific name parameter
 *
 * Example:
 * - `/www/api/v1/brains/check_name?name=MyBrain` -> `/www/api/v1/brains/check_name`
 * - `/www/api/v1/desks/123/rules/check_name?name=MyRule` -> `/www/api/v1/desks/123/rules/check_name`
 */
function getEndpointKey(endpoint: string): string {
  // Remove query parameters to get the base endpoint
  const url = new URL(endpoint, 'http://dummy'); // Use dummy base for parsing
  return url.pathname;
}

// Track pending debounce state per endpoint key (base endpoint pattern)
interface PendingState {
  timeout: NodeJS.Timeout | null;
  resolvers: Array<{
    endpoint: string; // Store the actual endpoint for the API call
    resolve: (value: { available: boolean; count: number }) => void;
    reject: (error: unknown) => void;
  }>;
  apiCallInProgress: boolean; // Track if an API call is currently in progress
}

const pendingStates = new Map<string, PendingState>();

// Debounce delay: 500ms
const DEBOUNCE_DELAY = 500;

function flushPendingChecks(endpointKey: string) {
  const state = pendingStates.get(endpointKey);
  if (!state || state.apiCallInProgress || state.resolvers.length === 0) {
    return;
  }

  state.timeout = null;
  state.apiCallInProgress = true;

  const waiting = state.resolvers;
  state.resolvers = [];

  const latestEndpoint = waiting[waiting.length - 1].endpoint;

  checkNameAvailability(latestEndpoint)
    .then((result) => {
      waiting.forEach(({ resolve }) => resolve(result));
    })
    .catch((error) => {
      waiting.forEach(({ reject }) => reject(error));
    })
    .finally(() => {
      state.apiCallInProgress = false;
      if (state.resolvers.length > 0) {
        state.timeout = setTimeout(() => flushPendingChecks(endpointKey), DEBOUNCE_DELAY);
      } else {
        pendingStates.delete(endpointKey);
      }
    });
}

/**
 * Debounced version of checkNameAvailability
 * Prevents excessive API calls by waiting 500ms after the last call
 *
 * @param endpoint - The endpoint URL to check name availability
 * @returns Promise with availability result
 *
 * @example
 * const { available } = await debouncedCheckNameAvailability(
 *   brainsEndpoints.checkName('my-brain', {})
 * );
 */
export async function debouncedCheckNameAvailability(
  endpoint: string
): Promise<{ available: boolean; count: number }> {
  const endpointKey = getEndpointKey(endpoint);

  return new Promise<{ available: boolean; count: number }>((resolve, reject) => {
    let state = pendingStates.get(endpointKey);

    if (!state) {
      state = { timeout: null, resolvers: [], apiCallInProgress: false };
      pendingStates.set(endpointKey, state);
    }

    state.resolvers.push({ endpoint, resolve, reject });

    // A request is already out; this caller is picked up by the follow-up round it schedules
    if (state.apiCallInProgress) {
      return;
    }

    clearTimeout(state.timeout);
    state.timeout = setTimeout(() => flushPendingChecks(endpointKey), DEBOUNCE_DELAY);
  });
}
